'use strict';

function money(v) {
  const n = typeof v === 'number' ? v : parseFloat(v);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

function formatUsd(n) {
  return `$${money(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function coRecordId(record, eventType) {
  if (!record) return null;
  if (record.change_order_id != null && (eventType === 'document_added' || eventType === 'document_replaced')) {
    return record.change_order_id;
  }
  return record.id ?? record.change_order_id ?? null;
}

function coById(entries, id) {
  if (!id || !entries) return null;
  return entries.find((e) => e.id === id) ?? null;
}

function payAppById(apps, payAppId) {
  if (!payAppId || !apps) return null;
  return apps.find((a) => a.id === payAppId) ?? null;
}

function payAppNumber(pa) {
  if (pa?.app_number != null) return pa.app_number;
  return null;
}

function signedAmount(amount) {
  const abs = formatUsd(Math.abs(amount));
  if (amount < 0) return `−${abs}`;
  if (amount > 0) return `+${abs}`;
  return abs;
}

function billingPct(project) {
  if (!project) return null;
  const contract = parseFloat(project.original_contract ?? '0') || 0;
  const cos = parseFloat(project.change_order_total ?? '0') || 0;
  const billed = parseFloat(project.total_billed ?? '0') || 0;
  const retained = parseFloat(project.retention_held ?? '0') || 0;
  const contractTotal = contract + cos;
  if (contractTotal <= 0) return null;
  const billedGross = billed + retained;
  return Math.round((billedGross / contractTotal) * 1000) / 10;
}

function docFileName(event) {
  const fromDesc = String(event.description || '').trim();
  if (fromDesc && !/uploaded|removed/i.test(fromDesc)) return fromDesc;
  const fromTitle = String(event.title || '');
  const m = fromTitle.match(/·\s*(.+)$/);
  return m ? m[1].trim() : '';
}

function pulseTimelineMeta(event) {
  switch (event.type) {
    case 'pay_app_submitted':
    case 'pay_app_created':
    case 'retainage_release_created':
      return 'Billing · Submitted';
    case 'payment_recorded':
      return 'Payment · Received';
    case 'retainage_release_paid':
      return 'Payment · Retainage';
    case 'change_order_approved':
      return 'Change order';
    case 'contract_created':
      return 'Original agreement';
    case 'document_added':
    case 'document_replaced':
      return 'Supporting document';
    case 'document_removed':
      return 'Document';
    case 'project_completion_set':
      return 'Closeout';
    case 'closeout_generated':
      return 'Closeout';
    default:
      if (/sent/i.test(event.title || '') || /sent/i.test(event.description || '')) {
        return 'Delivery';
      }
      return (event.type || '').replace(/_/g, ' ');
  }
}

function pulseTimelineTitle(event, ctx = {}) {
  const { coEntries, payApps, payments, project } = ctx;
  const co = coById(coEntries, coRecordId(event.record, event.type));
  const payAppId = event.record?.pay_app_id ?? (event.record?.kind === 'pay_app' ? event.record?.id : null);
  const pa = payAppById(payApps, payAppId);

  if (event.type === 'change_order_approved' && co) {
    const label = co.billing_label ? ` · ${co.billing_label}` : '';
    return `CO-${co.co_number} '${co.title}' added · ${signedAmount(co.amount)} → contract now ${formatUsd(co.running_contract)}${label}`;
  }

  if (event.type === 'pay_app_submitted') {
    const n = payAppNumber(pa);
    const due = event.amount_cents != null
      ? formatUsd(event.amount_cents / 100)
      : (pa?.amount_due != null ? formatUsd(parseFloat(String(pa.amount_due))) : null);
    const hash = n != null ? `#${n}` : '';
    const duePart = due ? ` · ${due} due` : '';
    return `Pay app ${hash} submitted${duePart}`;
  }

  if (event.type === 'payment_recorded' || event.type === 'retainage_release_paid') {
    const n = payAppNumber(pa);
    const paid = event.amount_cents != null ? formatUsd(event.amount_cents / 100) : null;
    const dueAmt = pa?.amount_due != null ? parseFloat(String(pa.amount_due)) : NaN;
    const paidAmt = pa?.amount_paid != null ? parseFloat(String(pa.amount_paid)) : 0;
    const openAmt = Number.isFinite(dueAmt) ? Math.max(0, dueAmt - (Number.isFinite(paidAmt) ? paidAmt : 0)) : NaN;
    const open = Number.isFinite(openAmt) && openAmt > 0 ? formatUsd(openAmt) : null;
    const paPart = n != null ? ` · PA #${n}` : '';
    const paidPart = paid ? ` · ${paid}` : '';
    const openPart = open ? ` · open now ${open}` : '';
    const prefix = event.type === 'retainage_release_paid' ? 'Retainage release paid' : 'Payment recorded';
    return `${prefix}${paPart}${paidPart}${openPart}`;
  }

  if (event.type === 'contract_created') {
    const val = event.amount_cents != null ? formatUsd(event.amount_cents / 100) : null;
    const sovMatch = String(event.description || event.title || '').match(/(\d+)\s*sov/i);
    const sovPart = sovMatch ? ` · ${sovMatch[1]} SOV lines` : '';
    const valPart = val ? ` · ${val}` : '';
    return `Contract created${valPart}${sovPart}`;
  }

  if (event.type === 'document_added' && event.record?.change_order_id != null && co) {
    const file = docFileName(event) || co.attachments?.[0]?.original_name || co.attachments?.[0]?.filename || 'document';
    return `CO-${co.co_number} document added · ${file}`;
  }

  if (event.type === 'document_added' || event.type === 'document_replaced') {
    const file = docFileName(event);
    if (file) return `${event.title || 'Document added'} · ${file}`;
  }

  if (/sent/i.test(event.title || '') || (event.type === 'edit' && /sent|emailed/i.test(String(event.description || event.title || '')))) {
    const n = payAppNumber(pa);
    const dest = String(event.description || '').replace(/^.*sent\s+to\s*/i, '').trim() || 'owner';
    return n != null ? `Pay app #${n} sent to ${dest}` : (event.title || 'Pay app sent');
  }

  if (event.type === 'pay_app_created') {
    const n = payAppNumber(pa);
    return n != null ? `Pay app #${n} created` : (event.title || 'Pay app created');
  }

  if (event.type === 'retainage_release_created') {
    const n = payAppNumber(pa);
    return n != null ? `Retainage release · pay app #${n} created` : (event.title || 'Retainage release created');
  }

  return event.title || '';
}

function pulseCoContractFlow(event, ctx = {}) {
  const co = coById(ctx.coEntries, coRecordId(event.record, event.type));
  if (event.type === 'change_order_approved' && co) {
    const previous = money(co.running_contract) - money(co.amount);
    return {
      kind: 'change_order',
      previous,
      delta: money(co.amount),
      current: money(co.running_contract),
      coNumber: co.co_number,
    };
  }
  if (event.type === 'contract_created') {
    const amount = event.amount_cents != null ? event.amount_cents / 100 : null;
    if (amount == null) return null;
    return { kind: 'original', amount: money(amount) };
  }
  return null;
}

function pulseCoBillingLink(event, ctx = {}) {
  const co = coById(ctx.coEntries, coRecordId(event.record, event.type));
  if (event.type !== 'change_order_approved' || !co) return null;
  if (!co.pay_app_id) return null;
  const partial = co.billing_status === 'partly_billed';
  const label = co.billing_label || (co.pay_app_number != null ? `Billed (PA #${co.pay_app_number})` : 'View pay app');
  return {
    label,
    payAppId: co.pay_app_id,
    partial,
    payAppNumber: co.pay_app_number,
  };
}

function pulseTimelineAmountDisplay(event, ctx = {}) {
  if (event.type === 'change_order_approved') {
    const co = coById(ctx.coEntries, coRecordId(event.record, event.type));
    if (co) return signedAmount(co.amount);
  }
  if (event.amount_cents != null) {
    const types = new Set([
      'pay_app_submitted', 'payment_recorded', 'retainage_release_paid',
      'contract_created', 'pay_app_created', 'retainage_release_created',
    ]);
    if (types.has(event.type)) {
      const val = formatUsd(event.amount_cents / 100);
      return event.type === 'payment_recorded' || event.type === 'retainage_release_paid' ? val : val;
    }
  }
  return '—';
}

function pulseTimelineAmountCsv(event, ctx = {}) {
  const disp = pulseTimelineAmountDisplay(event, ctx);
  return disp === '—' ? '' : disp;
}

export {
  pulseTimelineTitle,
  pulseTimelineMeta,
  pulseTimelineAmountDisplay,
  pulseTimelineAmountCsv,
  pulseCoContractFlow,
  pulseCoBillingLink,
  formatUsd,
};
